// src/components/AppRoutes.jsx
import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import ScrollToTop from './components/scrolltotop.jsx';

// Auth Components
import ProtectedRouteAuth from './components/ProtectedRouteAuth.js';
import PublicRoute from './components/PublicRoute';

// Public Pages
import Home from './public-routes/homepage/homepage';
import AboutUS from './public-routes/about/about';
import ContactForm from './public-routes/ContactPage/ContactPage';
import ThankYou from './public-routes/ThankYouPage/ThankYouPage.jsx';
import FAQ from './public-routes/FAQSection/FAQSection';
import Privacy from './public-routes/policy/policy';
import Dataprocess from './public-routes/policy/data-processing.jsx';
import Terms from './public-routes/Terms/Terms';
import Contract from './public-routes/Contarct/Contract.jsx';
// Blog Pages
import BlogPage from './public-routes/blog/BlogPage';
import BlogPostPage from './public-routes/blog/BlogPostPage';

// Auth Pages
import Login from './public-routes/auth/Login';
import Register from './public-routes/auth/Register';
import ForgotPassword from './public-routes/auth/ForgotPassword';
import VerifyMFA from './public-routes/auth/VerifyMFA';
import MFASetup from './public-routes/auth/MFASetup';
import Unauthorized from './components/common/Unauthorized.jsx';

// Sectors Pages
import SectorsPage from './public-routes/sectors/SectorsPage';
import EcommerceSector from './public-routes/sectors/EcommerceSector.jsx';
import HospitalitySector from './public-routes/sectors/HospitalitySector.jsx';
import SassSector from './public-routes/sectors/SassSector.jsx';
import TravelSector from './public-routes/sectors/TravleSector.jsx';
import B2BSector from './public-routes/sectors/B2BSector.jsx';
import FinancialSector from './public-routes/sectors/FinancialSector.jsx';
import FintechSector from './public-routes/sectors/FintechSector.jsx';
import AllSectors from './public-routes/sectors/AllSectors.jsx';

// Services Pages
import ServicesLayout from './components/layout/ServicesLayout.jsx';
import ServicesOverview from './components/layout/ServicesOverview.jsx';

// SEO Services
import SEOPage from './public-routes/seo/SEOPage.jsx';
import AiSEOPage from './public-routes/seo/AISEOPage.jsx';
import GEOPage from './public-routes/seo/GEOPage.jsx';
import TechnicalSEOPage from './public-routes/seo/TechnicalSEOPage.jsx';
import EcommerceSEOPage from './public-routes/seo/EcommerceSEOPage.jsx';
import InternationalSEOPage from './public-routes/seo/InternationalSEO.jsx';

// Paid Media Services
import PaidMediaPage from './public-routes/paidmedia/PaidMedia.jsx';
import PpcPage from './public-routes/paidmedia/ppc.jsx';
import PaidSocialPage from './public-routes/paidmedia/PaidSocialPage.jsx';
import ProgrammaticPage from './public-routes/paidmedia/ProgrammaticPage.jsx';
import MetaAdvertisingPage from './public-routes/paidmedia/MetaAdvertisingPage.jsx';
import PPCConsultancyPage from './public-routes/paidmedia/ppconsulate.jsx';

// Data & AI Services
import DataAiPage from './public-routes/data/DataSciencePage.jsx';
import AnalyticsPage from './public-routes/data/DataAnalyctics.jsx';
import TagManagementPage from './public-routes/data/TagManagementPage.jsx';

// Strategy Services
import StrategyPage from './public-routes/Strategy/Strategy.jsx';
import AuditPage from './public-routes/Strategy/Audit.jsx';
import MarketResearchPage from './public-routes/Strategy/MarketResearchPage.jsx';
import InternationalMarketingPage from './public-routes/Strategy/InternationalMarketingPage.jsx';
import WebDesign from './public-routes/Experience/WebDesign'
import MobileDesign from './public-routes/Experience/MobilePage'
// Experience Services
import ExperiencePage from './public-routes/Experience/Experience.jsx';
import CreativePage from './public-routes/Experience/CreativePage.jsx';
import CROPage from './public-routes/Experience/CROPage.jsx';
import TechnologyPage from './public-routes/Experience/Technology.jsx';
import VideoPage from './public-routes/Experience/Video.jsx';

// Customer Pages
import CustomerDashboard from './customer-routes/dashboard/Dashboard';
import CustomerOverview from './customer-routes/dashboard/Overview';
import Security from './customer-routes/security/Security';
import Campaigns from './customer-routes/Campaigns/Campaigns';
import AdsPage from './customer-routes/Ads/Ads';
import CustomerWallet from './customer-routes/wallet/Wallet';
import Deposit from './customer-routes/wallet/Deposit';
import TwoFA from './customer-routes/security/TwoFA';

// Admin Pages
import AdminDashboard from './admin-routes/dashboard/AdminDashboard';
import UserManagement from './admin-routes/users/UserManagement';
import AdminServices from './admin-routes/adminServices/index.jsx';
import AdminLogin from './admin-routes/Auth/AdminLogin.jsx';

// Layouts
import HomeLayout from './components/layout/HomeLayout';
import CustomerLayout from './components/layout/CustomerLayout';
import AdminLayout from './components/layout/AdminLayout.jsx';

// Layout Wrappers
const HomeRoutesWrapper = () => {
    return (
        <HomeLayout>
            <Outlet />
        </HomeLayout>
    );
};

const CustomerRoutesWrapper = () => {
    return (
        <ProtectedRouteAuth allowedRoles={['customer', 'user', 'client']}>
            <CustomerLayout>
                <Outlet />
            </CustomerLayout>
        </ProtectedRouteAuth>
    );
};

const AdminRoutesWrapper = () => {
    return (
        <ProtectedRouteAuth requireAdmin allowedRoles={['admin', 'administrator', 'superadmin']}>
            <AdminLayout>
                <Outlet />
            </AdminLayout>
        </ProtectedRouteAuth>
    );
};

// Services Page Wrapper - Includes Header and Footer but not the full HomeLayout
const ServicesPageWrapper = () => {
    return (
        <div>
            <main>
                <Outlet />
            </main>
        </div>
    );
};

const AppRoutes = () => {
    return (
        <>
            <ScrollToTop /> {/* ✅ ADD THIS - It will work for all routes */}
            <Routes>
                {/* Public Auth Routes - Redirect if already logged in */}
                <Route path="/login" element={
                    <PublicRoute>
                        <Login />
                    </PublicRoute>
                } />

                <Route path="/register" element={
                    <PublicRoute>
                        <Register />
                    </PublicRoute>
                } />

                <Route path="/forgot-password" element={
                    <PublicRoute>
                        <ForgotPassword />
                    </PublicRoute>
                } />

                <Route path="/verify-mfa" element={
                    <PublicRoute>
                        <VerifyMFA />
                    </PublicRoute>
                } />

                <Route path="/setup-mfa" element={
                    <PublicRoute>
                        <MFASetup />
                    </PublicRoute>
                } />

                <Route path="/admin/login" element={
                    <PublicRoute>
                        <AdminLogin />
                    </PublicRoute>
                } />

                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Protected Admin Routes */}
                <Route path="/admin" element={<AdminRoutesWrapper />}>
                    <Route index element={<Navigate to="dashboard" replace />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="services" element={<AdminServices />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="settings" element={<AdminDashboard />} />
                </Route>

                {/* Public Routes with Home Layout */}
                <Route element={<HomeRoutesWrapper />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<AboutUS />} />
                    <Route path="/about-us" element={<AboutUS />} />
                    <Route path="/contact" element={<ContactForm />} />
                    <Route path="/contact-form" element={<ContactForm />} />
                    <Route path="/thank-you/:id" element={<ThankYou />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/privacy&policy" element={<Privacy />} />
                    <Route path="/data-processing" element={<Dataprocess />} />
                    <Route path="/reachtix-privacy-policy" element={<Privacy />} />
                    <Route path="/privacy-policy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/contract" element={<Contract />} />
                    <Route path="/sectors" element={<SectorsPage />} />
                    <Route path="/sectors/ecommerce" element={<EcommerceSector />} />
                    <Route path="/sectors/hospitality" element={<HospitalitySector />} />
                    <Route path="/sectors/sass" element={<SassSector />} />
                    <Route path="/sectors/travel" element={<TravelSector />} />
                    <Route path="/sectors/b2b" element={<B2BSector />} />
                    <Route path="/sectors/finance" element={<FinancialSector />} />
                    <Route path="/sectors/fintech" element={<FintechSector />} />
                    <Route path="/sectors/all-sectors" element={<AllSectors />} />

                    {/* Blog Routes */}
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/blog/category/:category" element={<BlogPage />} />
                    <Route path="/blog/tag/:tag" element={<BlogPage />} />
                    <Route path="/blog/author/:author" element={<BlogPage />} />
                </Route>

                {/* Services Routes - Separate from HomeLayout */}
                <Route element={<ServicesPageWrapper />}>
                    <Route path="/services" element={<ServicesLayout />}>
                        <Route index element={<ServicesOverview />} />

                        {/* SEO Services */}
                        <Route path="seo" element={<SEOPage />} />
                        <Route path="seo/ai-seo" element={<AiSEOPage />} />
                        <Route path="seo/geo" element={<GEOPage />} />
                        <Route path="seo/technical" element={<TechnicalSEOPage />} />
                        <Route path="seo/ecommerce" element={<EcommerceSEOPage />} />
                        <Route path="seo/international" element={<InternationalSEOPage />} />

                        {/* Paid Media Services */}
                        <Route path="paid-media" element={<PaidMediaPage />} />
                        <Route path="paid-media/ppc" element={<PpcPage />} />
                        <Route path="paid-media/paid-social" element={<PaidSocialPage />} />
                        <Route path="paid-media/programmatic" element={<ProgrammaticPage />} />
                        <Route path="paid-media/meta-advertising" element={<MetaAdvertisingPage />} />
                        <Route path="paid-media/ppc-consultancy" element={<PPCConsultancyPage />} />

                        {/* Strategy & Data & AI Services */}
                        <Route path="strategy/data-ai" element={<DataAiPage />} />
                        <Route path="strategy/analytics" element={<AnalyticsPage />} />
                        <Route path="strategy/tag-management" element={<TagManagementPage />} />
                        <Route path="strategy" element={<StrategyPage />} />
                        <Route path="strategy/audits-consultancy" element={<AuditPage />} />
                        <Route path="strategy/market-research" element={<MarketResearchPage />} />
                        <Route path="strategy/international-marketing" element={<InternationalMarketingPage />} />

                        {/* Branding Services */}
                        <Route path="branding" element={<ExperiencePage />} />
                        <Route path="branding/creative" element={<CreativePage />} />
                        <Route path="branding/cro" element={<CROPage />} />
                        <Route path="branding/technology" element={<TechnologyPage />} />
                        <Route path="branding/video" element={<VideoPage />} />
                        <Route path="branding/WebDesign" element={<WebDesign />} />
                        <Route path="branding/mobiledesign" element={<MobileDesign />} />
                    </Route>
                </Route>

                {/* Customer Protected Routes */}
                <Route element={<CustomerRoutesWrapper />}>
                    <Route path="/panel">
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<CustomerDashboard />} />
                        <Route path="overview" element={<CustomerOverview />} />
                        <Route path="wallet/:currency" element={<CustomerWallet />} />
                        <Route path="deposit" element={<Deposit />} />
                        <Route path="ads" element={<AdsPage />} />
                        <Route path="campaigns" element={<Campaigns />} />
                        <Route path="security" element={<Security />} />
                        <Route path="security/2fa" element={<TwoFA />} />
                    </Route>
                </Route>

                {/* 404 Not Found */}
                <Route path="/404" element={<div>Page Not Found</div>} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </>
    );
};

export default AppRoutes;